// Client-side activity tracker. Batches user-facing events (page views,
// searches, item views, filter changes) and POSTs them to /api/activity/track
// every FLUSH_MS or on page hide. Server enforces a whitelist of action
// names — fake "writes" (e.g. candidate_created) cannot be smuggled in.
import { api, API } from "./api";

const FLUSH_MS = 5000;
const MAX_BUFFER = 50;
const buffer = [];
let timer = null;

function _enqueue(event) {
  buffer.push({ ...event, _ts: Date.now() });
  if (buffer.length >= MAX_BUFFER) {
    _flush();
    return;
  }
  if (!timer) {
    timer = setTimeout(_flush, FLUSH_MS);
  }
}

async function _flush() {
  if (timer) { clearTimeout(timer); timer = null; }
  if (buffer.length === 0) return;
  const events = buffer.splice(0, buffer.length).map(({ _ts, ...e }) => e); // eslint-disable-line no-unused-vars
  try {
    await api.post("/activity/track", { events });
  } catch {
    // Re-queue silently on failure — keep small to avoid an unbounded grow.
    if (buffer.length < MAX_BUFFER) buffer.unshift(...events.slice(0, MAX_BUFFER - buffer.length));
  }
}

// Best-effort flush on tab close — uses sendBeacon which fires even if the
// page is unloading. Falls back to the normal axios call when sendBeacon
// isn't available (mostly never).
function _beaconFlush() {
  if (buffer.length === 0) return;
  const events = buffer.splice(0, buffer.length).map(({ _ts, ...e }) => e); // eslint-disable-line no-unused-vars
  const token = localStorage.getItem("mercy_token");
  if (!token) return;
  const blob = new Blob([JSON.stringify({ events })], { type: "application/json" });
  try {
    // sendBeacon doesn't allow custom headers (no Authorization), so embed
    // the token in the URL — backend has a fallback that accepts it from
    // the query string for this endpoint.
    navigator.sendBeacon(`${API}/activity/track?token=${encodeURIComponent(token)}`, blob);
  } catch {
    /* swallow */
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("pagehide", _beaconFlush);
  window.addEventListener("beforeunload", _beaconFlush);
  // Periodic flush even when user is idle (catches mid-tab events)
  setInterval(_flush, FLUSH_MS);
}

// ---------- Public API ----------

/** Log a route navigation. Called by the React Router listener. */
export function trackPageView(path, label = "") {
  _enqueue({
    action: "page_viewed",
    entity_type: _moduleFromPath(path),
    entity_label: label || path,
    module: _moduleFromPath(path),
    details: { path },
  });
}

/** Log a search query. Debounce at the call site. */
export function trackSearch(module, query, extra = {}) {
  if (!query || !query.trim()) return;
  _enqueue({
    action: "searched",
    entity_type: module,
    entity_label: query.slice(0, 60),
    module,
    details: { q: query, ...extra },
  });
}

/** Log opening an entity detail dialog / drill-down view. */
export function trackView(entityType, entityId, entityLabel = "", module = "") {
  _enqueue({
    action: "viewed",
    entity_type: entityType,
    entity_id: entityId || "",
    entity_label: entityLabel,
    module: module || entityType,
  });
}

/** Log a filter / status / sort change so we know how staff slice data. */
export function trackFilter(module, label, details = {}) {
  _enqueue({
    action: "filtered",
    entity_type: module,
    entity_label: label,
    module,
    details,
  });
}

/** Log an interesting button click (PDF export, print, share, etc.). */
export function trackClick(action, module, label = "", details = {}) {
  _enqueue({
    action: "clicked",
    entity_type: module,
    entity_label: `${action}: ${label}`.trim(),
    module,
    details: { ui_action: action, ...details },
  });
}

/** Log an in-page tab switch (e.g. Accounting tab → Entries/Recurring/...). */
export function trackTab(module, tab) {
  _enqueue({
    action: "tab_opened",
    entity_type: module,
    entity_label: tab,
    module,
    details: { tab },
  });
}

// ---------- React hook ----------
// Debounced search tracker. Call once at the top of a page with the
// current `query` state and the module name; it'll log a `searched` event
// 1.2s after the user stops typing (matches "real query" timing without
// flooding the log on every keystroke).
import { useEffect, useRef } from "react";
export function useTrackedSearch(module, query, extra = {}) {
  const lastRef = useRef("");
  useEffect(() => {
    if (!query) return;
    const trimmed = String(query).trim();
    if (!trimmed || trimmed === lastRef.current) return;
    const t = setTimeout(() => {
      lastRef.current = trimmed;
      trackSearch(module, trimmed, extra);
    }, 1200);
    return () => clearTimeout(t);
  // We deliberately exclude `extra` from deps — a stable details object
  // is the caller's responsibility, and re-firing on object identity
  // would re-track on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module, query]);
}

// ---------- Helpers ----------

function _moduleFromPath(path) {
  if (!path) return "hub";
  const seg = path.split("/").filter(Boolean)[0];
  return seg || "hub";
}
